
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { KanbanCard } from './KanbanCard';
import { CreateCardButton } from './CreateCardButton';
import { ColumnRename } from './ColumnRename';
import { ColumnFilter, type ColumnFilters } from './ColumnFilter';
import { ColumnContextMenu } from './ColumnContextMenu';
import { ColumnDragTarget } from '@/components/collaboration/ColumnDragTarget';
import { Button } from '@/components/ui/button';
import { Trash2, Crown, GripVertical, MoreVertical } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { useDragStateContext } from '@/contexts/DragStateContext';
import { useAuth } from '@/hooks/useAuth';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useState, useMemo, useCallback, useRef } from 'react';
import type { Tables } from '@/integrations/supabase/types';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Archive } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

type Column = Tables<'columns'>;
type Card = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

interface KanbanColumnProps {
    column: Column;
    cards: Card[];
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    onRegisterCardForAnimation?: (cardId: string, handler: (updateFn: () => void) => void) => void;
    onUnregisterCardForAnimation?: (cardId: string) => void;
}

export const KanbanColumn = ({ column, cards, dragHandleProps, onRegisterCardForAnimation,
    onUnregisterCardForAnimation }: KanbanColumnProps) => {
    const { deleteColumn, updateCard, showArchivedCards } = useBoardContext();
    const { dragState } = useDragStateContext();
    const { user } = useAuth();

    // **PHASE 1: Stabilized refs for card animation management**
    const cardAnimationHandlersRef = useRef<Map<string, (updateFn: () => void) => void>>(new Map());

    // Column-specific filter state
    const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
        tags: [],
        endDate: null,
        assignedTo: null,
        progressState: null,
        category: null,
        sortBy: null,
        sortOrder: 'asc'
    });

    // **PHASE 1: Stabilized card registration function**
    const handleRegisterCardForAnimation = useCallback((cardId: string, handler: (updateFn: () => void) => void) => {
        cardAnimationHandlersRef.current.set(cardId, handler);
        onRegisterCardForAnimation?.(cardId, handler);
    }, [onRegisterCardForAnimation]);

    // **PHASE 1: Stabilized card unregistration function**
    const handleUnregisterCardForAnimation = useCallback((cardId: string) => {
        cardAnimationHandlersRef.current.delete(cardId);
        onUnregisterCardForAnimation?.(cardId);
    }, [onUnregisterCardForAnimation]);

    // **PHASE 1: Stabilized remote update callback**
    const createRemoteUpdateHandler = useCallback((cardId: string) => {
        return (handler: (updateFn: () => void) => void) => {
            handleRegisterCardForAnimation(cardId, handler);
            // Return cleanup function
            return () => handleUnregisterCardForAnimation(cardId);
        };
    }, [handleRegisterCardForAnimation, handleUnregisterCardForAnimation]);

    // Get users dragging over this column, excluding current user
    const dragUsersOverColumn = useMemo(() => {
        const allDragUsers = Object.values(dragState).flat();
        return allDragUsers.filter(dragUser => 
            dragUser.target_column_id === column.id && 
            dragUser.user_id !== user?.id
        );
    }, [dragState, column.id, user?.id]);

    // Apply column-specific filters and sorting
    const filteredCards = useMemo(() => {
        let filtered = [...cards];

        // Apply tag filter
        if (columnFilters.tags.length > 0) {
            filtered = filtered.filter(card =>
                card.tags && Array.isArray(card.tags) &&
                columnFilters.tags.some(tagId => card.tags.includes(tagId))
            );
        }

        // Apply end date filter
        if (columnFilters.endDate) {
            const now = new Date();
            const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

            filtered = filtered.filter(card => {
                if (columnFilters.endDate === 'no_date') {
                    return !card.end_date;
                }
                if (!card.end_date) return false;

                const endDate = new Date(card.end_date);

                if (columnFilters.endDate === 'overdue') {
                    return endDate < now;
                }
                if (columnFilters.endDate === 'due_soon') {
                    return endDate >= now && endDate <= sevenDaysFromNow;
                }
                return true;
            });
        }

        // Apply assigned user filter
        if (columnFilters.assignedTo) {
            filtered = filtered.filter(card => card.assigned_to === columnFilters.assignedTo);
        }

        // Apply progress state filter
        if (columnFilters.progressState) {
            filtered = filtered.filter(card => card.progress_state === columnFilters.progressState);
        }

        // Apply category filter
        if (columnFilters.category) {
            filtered = filtered.filter(card => card.category === columnFilters.category);
        }

        // Apply sorting
        if (columnFilters.sortBy) {
            filtered.sort((a, b) => {
                let aValue: any;
                let bValue: any;
                const progressOrder = { 'not_started': 0, 'in_progress': 1, 'completed': 2 };

                switch (columnFilters.sortBy) {
                    case 'title':
                        aValue = a.title?.toLowerCase() || '';
                        bValue = b.title?.toLowerCase() || '';
                        break;
                    case 'end_date':
                        aValue = a.end_date ? new Date(a.end_date).getTime() : 0;
                        bValue = b.end_date ? new Date(b.end_date).getTime() : 0;
                        break;
                    case 'created_at':
                        aValue = new Date(a.created_at).getTime();
                        bValue = new Date(b.created_at).getTime();
                        break;
                    case 'progress_state':

                        aValue = progressOrder[a.progress_state as keyof typeof progressOrder] || 0;
                        bValue = progressOrder[b.progress_state as keyof typeof progressOrder] || 0;
                        break;
                    default:
                        aValue = a.position;
                        bValue = b.position;
                }

                if (aValue < bValue) return columnFilters.sortOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return columnFilters.sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        } else {
            // Default sorting by position
            filtered.sort((a, b) => a.position - b.position);
        }

        return filtered;
    }, [cards, columnFilters]);

    const handleArchiveAllCards = async () => {
        try {
            // Archive all non-archived cards in this column
            const cardsToArchive = cards.filter(card => !card.archived);

            await Promise.all(
                cardsToArchive.map(card =>
                    updateCard({
                        cardId: card.id,
                        updates: { archived: true }
                    })
                )
            );
        } catch (error) {
            console.error('Error archiving cards:', error);
        }
    };

    return (
        <ContextMenu>
            <ContextMenuTrigger asChild>
                <div
                    className="kanban-column flex flex-col min-w-80 max-w-80 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 relative"
                    style={{ height: 'calc(100vh - 180px)', minHeight: '500px', maxHeight: "100%" }}
                >
                    {/* Column drag target overlay - only show other users' drags */}
                    {dragUsersOverColumn.length > 0 && (
                        <ColumnDragTarget
                            dragUsers={dragUsersOverColumn}
                            columnTitle={column.title}
                        />
                    )}

                    {/* Fixed Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <div className="flex items-center gap-2 flex-1">
                            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                <GripVertical className="h-4 w-4 text-gray-400 dark:text-slate-400" />
                            </div>
                            <ColumnRename column={column} />
                            {column.is_completed_column && (
                                <Crown className="h-4 w-4 text-yellow-500" />
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-500">
                                {filteredCards.length}{cards.length !== filteredCards.length && `/${cards.length}`}
                            </Badge>
                            <ColumnFilter
                                column={column}
                                filters={columnFilters}
                                onFiltersChange={setColumnFilters}
                                cards={cards}
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteColumn(column.id)}
                                className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200"
                                    >
                                        <MoreVertical className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onClick={handleArchiveAllCards}
                                        disabled={cards.filter(card => !card.archived).length === 0}
                                    >
                                        <Archive className="h-4 w-4 mr-2" />
                                        Archive all cards ({cards.filter(card => !card.archived).length})
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>


                    {/* Scrollable Middle Section */}
                    <div className="flex-1 min-h-0">
                        <Droppable droppableId={column.id}>
                            {(provided, snapshot) => (
                                <ScrollArea className="h-full">
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`p-4 space-y-3 ${snapshot.isDraggingOver || dragUsersOverColumn.length > 0
                                                ? 'bg-blue-50 dark:bg-blue-950'
                                                : ''
                                            }`}
                                    >
                                        {filteredCards.map((card, index) => (
                                            <Draggable key={card.id} draggableId={card.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={cn(
                                                            "transition-transform",
                                                            snapshot.isDragging && "rotate-2 shadow-lg scale-105 z-50"
                                                        )}
                                                    >
                                                        <KanbanCard
                                                            card={card}
                                                            onRemoteUpdate={createRemoteUpdateHandler(card.id)}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                    <ScrollBar orientation="vertical" />
                                </ScrollArea>
                            )}
                        </Droppable>
                    </div>

                    {/* Fixed Footer */}
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <CreateCardButton columnId={column.id} />
                    </div>
                </div>
            </ContextMenuTrigger>
            <ContextMenuContent>
                <ContextMenuItem
                    onClick={handleArchiveAllCards}
                    disabled={cards.filter(card => !card.archived).length === 0}
                >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive all cards ({cards.filter(card => !card.archived).length})
                </ContextMenuItem>
            </ContextMenuContent>
        </ContextMenu>
    );
};
